// hooks/usePlanTracker.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc, setDoc, updateDoc, onSnapshot, increment } from 'firebase/firestore';
import { db } from '../firebase';

export const PLANS = {
  FREE: {
    name: 'Free',
    price: '0',
    period: 'Forever',
    description: 'Try it once. No account needed, no card required.',
    features: [
      '2 DMCA notices per month',
      'AI-generated DMCA letter',
      '20 verified platform agents',
      'PDF download',
      'Draw / upload signature',
      'Edit letter inline',
    ],
    limits: {
      qrPerMonth: 2,
      creditsPerHour: null,
      isUnlimited: false,
    },
    cta: 'Get started free',
    popular: false,
    stripeKey: null,
  },

  PRO: {
    name: 'Pro',
    price: '9',
    period: 'per month',
    description: 'For creators dealing with repeat infringement across platforms.',
    features: [
      '20 DMCA notices per month',
      'Everything in Starter',
      'Full notice history',
      'Copy to clipboard',
      'Resizable signature in PDF',
      'Re-download past notices',
    ],
    limits: {
      qrPerMonth: 20,
      creditsPerHour: null,
      isUnlimited: false,
    },
    cta: 'Start Pro',
    popular: true,
    stripeKey: 'Pro',
  },

  UNLIMITED: {
    name: 'Unlimited',
    price: '19',
    period: 'per month',
    description: 'No caps. For photographers, musicians, studios filing constantly.',
    features: [
      'Unlimited DMCA notices',
      'Everything in Pro',
      'Priority support',
    ],
    limits: {
      qrPerMonth: Infinity,
      creditsPerHour: null,
      isUnlimited: true,
    },
    cta: 'Go Unlimited',
    popular: false,
    stripeKey: 'Unlimited',
  },
};

export const usePlanTracker = (userId) => {
  const [currentPlan, setCurrentPlan] = useState('FREE');
  const [usage, setUsage] = useState({
    qrGenerated: 0,
    lastReset: null,
    hourlyUsage: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('free');

  // Ref used to guard against rapid double-clicks — prevents race condition
  // between canGenerateQR() check and consumeCredit() write propagating back
  const isConsuming = useRef(false);

  const planConfig = PLANS[currentPlan.toUpperCase()] || PLANS.FREE;

  const resetIfNewMonth = useCallback(async (usageRef, data) => {
    const now = new Date();
    const lastReset = data.lastReset ? new Date(data.lastReset) : null;

    const isDifferentMonth = !lastReset
      || now.getFullYear() !== lastReset.getFullYear()
      || now.getMonth() !== lastReset.getMonth();

    if (isDifferentMonth) {
      await updateDoc(usageRef, {
        qrGenerated: 0,
        lastReset: now.toISOString(),
        hourlyUsage: [],
      });
      setUsage({ qrGenerated: 0, lastReset: now, hourlyUsage: [] });
    }
  }, []);

  useEffect(() => {
    if (!userId) return;

    const usageRef = doc(db, 'usage', userId);
    const userRef  = doc(db, 'users', userId);

    const unsubUsage = onSnapshot(usageRef, async (snapshot) => {
      if (!snapshot.exists()) {
        await setDoc(usageRef, {
          qrGenerated: 0,
          lastReset: new Date().toISOString(),
          hourlyUsage: [],
        });
      } else {
        const data = snapshot.data();
        await resetIfNewMonth(usageRef, data);
        setUsage(data);
      }
      setLoading(false);
    });

    const unsubUser = onSnapshot(userRef, async (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const raw = (data.plan || 'free').toUpperCase();
        const mapped = raw === 'PREMIUM' ? 'UNLIMITED' : raw;
        setCurrentPlan(mapped);
        setSubscriptionStatus(data.subscriptionStatus || 'active');
      } else {
        // FIX: User document doesn't exist yet — create it with free plan defaults.
        // This happens when a user signs up but the document was never initialized.
        // Without this, the onSnapshot silently does nothing and the plan UI
        // never updates, making it impossible to set a plan in Firestore.
        try {
          await setDoc(userRef, {
            plan: 'free',
            subscriptionStatus: 'active',
            createdAt: new Date().toISOString(),
          });
        } catch (e) {
          console.error('Failed to create user document:', e);
          setError(e);
        }
      }
    });

    return () => { unsubUsage(); unsubUser(); };
  }, [userId, resetIfNewMonth]);

  const consumeCredit = async () => {
    // Guard against rapid double-clicks bypassing canGenerateQR
    if (isConsuming.current) return false;
    isConsuming.current = true;

    try {
      const usageRef  = doc(db, 'usage', userId);
      const usageSnap = await getDoc(usageRef);
      if (!usageSnap.exists()) return false;

      const data = usageSnap.data();
      const now  = new Date();
      const currentHour = now.getHours();

      const updatedHourly = (data.hourlyUsage || []).filter(e => {
        const entryDate = new Date(e.time);
        return (
          entryDate.getFullYear() === now.getFullYear() &&
          entryDate.getMonth()    === now.getMonth()    &&
          entryDate.getDate()     === now.getDate()     &&
          entryDate.getHours()    === currentHour
        );
      });
      updatedHourly.push({ time: now.toISOString() });

      await updateDoc(usageRef, {
        qrGenerated: increment(1),
        hourlyUsage: updatedHourly,
      });

      return true;
    } finally {
      isConsuming.current = false;
    }
  };

  const canGenerateQR = () => {
    if (isConsuming.current) return false;
    if (planConfig.limits.isUnlimited) return true;
    if ((usage.qrGenerated || 0) >= planConfig.limits.qrPerMonth) return false;
    if (
      planConfig.limits.creditsPerHour &&
      (usage.hourlyUsage || []).length >= planConfig.limits.creditsPerHour
    ) return false;
    return true;
  };

  const getLimitReason = () => {
    if ((usage.qrGenerated || 0) >= planConfig.limits.qrPerMonth)
      return 'Monthly notice limit reached. Please upgrade to continue.';
    if (
      planConfig.limits.creditsPerHour &&
      (usage.hourlyUsage || []).length >= planConfig.limits.creditsPerHour
    ) return 'Hourly limit reached. Please wait a bit.';
    return null;
  };

  const getUsageStats = () => {
    const generated  = usage.qrGenerated || 0;
    const limit      = planConfig.limits.qrPerMonth;
    const remaining  = planConfig.limits.isUnlimited
      ? 'Unlimited'
      : Math.max(0, limit - generated);

    return {
      qrGenerated: generated,
      qrRemaining: remaining,
      qrLimit: planConfig.limits.isUnlimited ? 'Unlimited' : limit,
      hourlyUsage: (usage.hourlyUsage || []).length,
      resetDate: usage.lastReset,
      canGenerate: canGenerateQR(),
      subscriptionStatus,
    };
  };

  const updatePlan = async (plan) => {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { plan: plan.toLowerCase() });
    setCurrentPlan(plan.toUpperCase());
  };

  return {
    currentPlan,
    planConfig,
    usage,
    loading,
    error,
    subscriptionStatus,
    consumeCredit,
    canGenerateQR,
    getLimitReason,
    getUsageStats,
    updatePlan,
  };
};